import _min from 'lodash/min'
import _isNan from 'lodash/isNaN'
import compose from 'recompose/compose'
import React, { useEffect, useRef, useState } from 'react'
import { connect as connectRedux } from 'react-redux'
import { connect as connectStaticText } from '../StaticText/connect'
import { getBoundActions } from '../../actions/index'
import Link from '../Link/index'
import Button, { TYPES } from '../Button/index'
import { URL_GAIA_PLUS } from '../../services/url/constants'
import { LIVE_ACCESS_SUBSCRIPTION_CODE } from '../../services/live-access-events/constants'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'
import { HEADING_TYPES, H1 } from '../Heading/index'

const zeroPad = (num, places) => String(num).padStart(places, '0')

const Countdown = ({
  staticText, countdown, localeTitleDate, title, speakerName,
  getLiveAccessNextEvent, language, eventRoute, isAnonymous, isLiveAccessMember, eventType,
}) => {
  const [remaining, setRemaining] = useState(countdown)

  const requestRef = useRef()
  const previousTimeRef = useRef()
  const timeoutRef = useRef()
  const animate = (time) => {
    if (previousTimeRef.current !== undefined) {
      const deltaTime = time - previousTimeRef.current
      setRemaining((prevRemaining) => (Math.max(0, prevRemaining - deltaTime)))
    }
    previousTimeRef.current = time
    requestRef.current = requestAnimationFrame(animate)
  }

  useEffect(() => {
    clearTimeout(timeoutRef.current)
    if (countdown) {
      timeoutRef.current = setTimeout(() => {
        getLiveAccessNextEvent({ language })
      }, _min([countdown, 10 * 60 * 1000]))
      if (_isNan(remaining)) {
        setRemaining(countdown)
      }
    }
  }, [countdown])

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate)
    return () => {
      clearTimeout(timeoutRef.current)
      cancelAnimationFrame(requestRef.current)
    }
  }, [])
  const _remaining = _isNan(remaining) ? 0 : remaining
  const days = Math.floor(_remaining / (24 * 60 * 60 * 1000))
  const daysms = _remaining % (24 * 60 * 60 * 1000)
  const hours = Math.floor((daysms) / (60 * 60 * 1000))
  const hoursms = _remaining % (60 * 60 * 1000)
  const minutes = Math.floor((hoursms) / (60 * 1000))
  const minutesms = _remaining % (60 * 1000)
  const seconds = Math.floor((minutesms) / (1000))

  const daysLabel = (days === 1) ? staticText.getIn(['data', 'day']) : staticText.getIn(['data', 'days'])
  const hoursLabel = (hours === 1) ? staticText.getIn(['data', 'hour']) : staticText.getIn(['data', 'hours'])
  const minutesLabel = (minutes === 1) ? staticText.getIn(['data', 'minute']) : staticText.getIn(['data', 'minutes'])
  const secondsLabel = (seconds === 1) ? staticText.getIn(['data', 'second']) : staticText.getIn(['data', 'seconds'])

  let url = URL_GAIA_PLUS
  let buttonTitle = staticText.getIn(['data', 'upgradeToWatch'])

  if (isAnonymous) {
    buttonTitle = staticText.getIn(['data', 'signUpToWatch'])
  }

  if (isLiveAccessMember) {
    url += `/${eventRoute}`
    buttonTitle = staticText.getIn(['data', 'viewDetails'])
  }

  // Hide "View Details" button for AMA events
  const showButton = eventType !== 'AMA'

  return (
    <div className="live-page-countdown__component">
      <div className="live-page-countdown__container">
        <div className="live-page-countdown__text_container">
          <div className="live-page-countdown__date">{localeTitleDate}</div>
          <H1 as={HEADING_TYPES.H2} className="live-page-countdown__title">
            {eventType === 'AMA' ? (
              <span style={{ fontSize: 'inherit', color: 'white' }}>{title}</span>
            ) : (
              <Link className="live-page-countdown__link" to={`${URL_GAIA_PLUS}/${eventRoute}`}>
                {title}
                <IconV2 type={ICON_TYPES.CHEVRON_RIGHT} />
              </Link>
            )}
          </H1>
          {speakerName
            && <div className="live-page-countdown__speaker">{staticText.getIn(['data', 'with'])} {speakerName}</div>
          }
        </div>
        <div className="live-page-countdown__timer_container">
          <div className="live-page-countdown__item">
            <span className="live-page-countdown__time">{zeroPad(days, 2)}:</span>
            <div className="live-page-countdown__label">{daysLabel}</div>
          </div>
          <div className="live-page-countdown__item">
            <span className="live-page-countdown__time">{zeroPad(hours, 2)}:</span>
            <div className="live-page-countdown__label">{hoursLabel}</div>
          </div>
          <div className="live-page-countdown__item">
            <span className="live-page-countdown__time">{zeroPad(minutes, 2)}:</span>
            <div className="live-page-countdown__label">{minutesLabel}</div>
          </div>
          <div className="live-page-countdown__item">
            <span className="live-page-countdown__time">{zeroPad(seconds, 2)}</span>
            <div className="live-page-countdown__label-seconds">{secondsLabel}</div>
          </div>
        </div>
        {showButton && (
          <div>
            <Button
              type={TYPES.SECONDARY}
              className="live-page-countdown__button"
              url={url}
            >
              {buttonTitle}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default compose(
  connectRedux(
    (state) => {
      const language = state.user.getIn(['data', 'language', 0], 'en')
      const countdown = state.liveAccessEvents.getIn(['eventDetails', language, 'data', 'countdown'])
      const localeTitleDate = state.liveAccessEvents.getIn(['eventDetails', language, 'data', 'event', 'localeTitleDate'])
      const title = state.liveAccessEvents.getIn(['eventDetails', language, 'data', 'event', 'title'])
      const speakerName = state.liveAccessEvents.getIn(['eventDetails', language, 'data', 'event', 'speakerName'])
      const isAnonymous = !state.auth.get('username')
      const hasEntitlements = state.auth.get('subscriptions') && state.auth.get('subscriptions').size > 0
      const eventRoute = state.liveAccessEvents.getIn(['eventDetails', language, 'data', 'event', 'route'])
      const eventType = state.liveAccessEvents.getIn(['eventDetails', language, 'data', 'event', 'type'])

      return {
        isAnonymous,
        isLiveAccessMember: !!state.auth.get('subscriptions') && state.auth.get('subscriptions').includes(LIVE_ACCESS_SUBSCRIPTION_CODE),
        user: state.user,
        language,
        hasEntitlements,
        countdown,
        localeTitleDate,
        title,
        speakerName,
        eventRoute,
        eventType,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPageSeo: actions.page.setPageSeo,
        getLiveAccessSchedule: actions.liveAccessEvents.getLiveAccessSchedule,
        getLiveAccessSessionDetails: actions.liveAccessEvents.getLiveAccessSessionDetails,
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
        getLiveAccessNextEvent: actions.liveAccessEvents.getLiveAccessNextEvent,
      }
    },
  ),
  connectStaticText({ storeKey: 'livePage' }),
)(Countdown)
